import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AuthService from '../services/AuthService';
import { IRootProps } from './types';
import { getTokenUrl } from '../services/AuthService/utilities';
import { useSourceContext } from './SourceContext';

const debug = require('debug')('omni:kit:context:AuthContext');

interface AuthContextProps {
  accessToken: string | null;
  isAuthenticated: boolean;
  context?: string;
}

export const AuthContext = createContext<AuthContextProps>({
  accessToken: AuthService.accessToken,
  isAuthenticated: Boolean(AuthService.accessToken),
  context: undefined,
});

interface ProviderProps {
  children?: React.ReactNode | Array<React.ReactNode>;
  props?: IRootProps & { context?: string };
}

export const AuthContextProvider = ({
  children,
  props,
}: ProviderProps): JSX.Element | null => {
  const source = useSourceContext();

  const context = props?.context;

  const [accessToken, setAccessToken] = useState(AuthService.accessToken);
  const [isReady, setIsReady] = useState(false);
  const isAuthenticated = useMemo(() => Boolean(accessToken), [accessToken]);

  const appKey = props?.appKey;

  useEffect(() => {
    /**
     * Bypass auth setup & token refresh logic when current context
     * does not need Authorization or should not have Authorization
     *
     * Examples
     * - Block Page when no authProviderId prop is provided.
     *
     * - Search tab at root of TCA:
     *   The root tab in container apps is designed to provide a 'tour' flow
     *   which is shown during first app launch after a user installs the app.
     *   The tour screens were not designed with any UX based on end user login.
     *   Attempting to refresh the end user access_token on the root app in TCA
     *   produces a 401 error due to a missing org_key on GH936H,
     *   generating noise in logs on backend and uncertainty around reliability
     *   of the service and behavior of the client-side application.
     */
    if (!props?.authProviderId) {
      setIsReady(true);

      return;
    }

    const serviceCleanup = AuthService.initialize();

    const setup = async (appKey: string) => {
      let tokenUrl = props?.tokenUrl; // passed as screen prop on mobile

      // when user switches apps on the web, we need to resolve the tokenUrl,
      // otherwise it will attempt to refresh token with the tokenUrl
      // from local storage, which may be the token url for a different app
      if (!tokenUrl) {
        try {
          tokenUrl = await getTokenUrl(appKey, props?.authProviderId);
        } catch {}
      }

      AuthService.loadCredentials({ ...props, source, tokenUrl })
        .then((token) => {
          setAccessToken(token);
        })
        .catch((e) => {
          debug(`Unable to initialize auth context: ${e}`);
        })
        .finally(() => {
          setIsReady(true);
        });
    };

    if (appKey) {
      setup(appKey);
    }

    return () => {
      serviceCleanup();
    };
    // root props will not change after mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKey]);

  const authCallback = useCallback(
    (data: { token: string | null }) => {
      const token = data.token;

      if (token && token !== accessToken) {
        setAccessToken(token);
      } else if (!token) {
        setAccessToken('');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken]
  );

  /**
   * Keep context up-to-date with any changes to credentials in AuthService
   *
   * TODO: Verify correctness of behavior where access token is cleared
   * when `fetchWithAuth` method fails in `kit/utilities/utilities.ts`
   * which may inadvertently unset the cached credentials.
   **/
  const removeListener = useRef<(() => void) | null>(null);
  useEffect(() => {
    // bypass auth setup logic when context does not need Authorization
    // e.g. Block Page when no authProviderId prop is provided and Search tour in TCA.
    if (!props?.authProviderId) {
      return;
    }

    if (isReady) {
      removeListener.current?.();

      removeListener.current = AuthService.addListener(
        'AuthorizationChanged',
        authCallback,
        context
      );

      // ref: https://reactjs.org/blog/2020/08/10/react-v17-rc.html#potential-issues
      const cleanup = removeListener.current;

      return () => {
        cleanup?.();
      };
    }

    /**
     * NOTE: Do not add 'accessToken' or 'authCallback' to dependencies array,
     * otherwise listeners will be added and removed in rapid succession,
     * leading to inconsistent auth state (e.g. user appears logged out
     * when the user is actually logged-in)
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, props?.authProviderId]);

  if (!isReady) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ accessToken, isAuthenticated, context }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  return useContext<AuthContextProps>(AuthContext);
};
